import { ListOrdered } from "lucide-react";
import {
  LamaPillarResultsParamsSchema,
  defaultLamaPillarResultsParams,
  type LamaPillarResultsOutput,
  type LamaPillarResultsParams,
} from "@playbookai/widget-schemas";
import { registerWidget } from "../registry.js";
import type { SqlWidgetDef } from "../types.js";
import { LamaCategoryResultsWidget } from "./Component.js";

/**
 * LAMA Results by Category — dashboard-only report section (plan §13.3).
 * `TView` is the shared category-results dataset output, served under BOTH
 * `lama/category-results@2` and the pre-rename `lama/pillar-results@1` — one
 * payload shape, two refs, for as long as a persisted connection row can
 * still name the old one (PB-191 Task 2a).
 *
 * PB-2882 deleted `lama_pillars`; the surviving grain is the version-scoped
 * `lama_categories.label`, so the kind is renamed to match (PB-191 D2/D19).
 */
export const lamaCategoryResultsDef: SqlWidgetDef<
  LamaPillarResultsParams,
  LamaPillarResultsOutput
> = {
  kind: "lamaCategoryResults",
  label: "Results by Category",
  modes: ["dashboard"],
  singleton: true,
  defaultSize: { w: 960, h: 400 },
  defaultParams: defaultLamaPillarResultsParams,
  Component: LamaCategoryResultsWidget,
  storage: "sql",
  schema: LamaPillarResultsParamsSchema,
  accent: "cyan",
  icon: ListOrdered,
  subtitle: "Current, previous, delta, and sparkline per category",
  defaultRoster: false,
  category: "single",
  layout: { col: "full", row: 4, width: 960, height: 400 },
  dashboard: {
    icon: ListOrdered,
    defaultWidth: "full",
    category: "Operations",
    supportsTitleOverride: true,
  },
};

/**
 * The pre-rename kind, kept RESOLVABLE rather than removed (PB-191 D35).
 *
 * This is an expand/contract seam, not a second widget. Persisted rows still
 * carry `'lamaPillarResults'` until Task 4's `UPDATE` runs, and that `UPDATE` is
 * a PostSync Argo hook — it fires only once the Deployment is healthy, so THIS
 * code is already serving before any row moves. Without the alias every widget
 * on the report page would render "Unsupported widget." during that window and —
 * the case that actually decides it — an app rollback after the rows have moved
 * would have no remedy but rolling forward, because Prisma has no down
 * migrations.
 *
 * A straight rename is unsafe in BOTH orders (rename first and every existing
 * row becomes unknown; migrate first and every row becomes a kind the registry
 * does not know), which is why this is an expand rather than a reordering.
 *
 * The label deliberately stays the OLD one: this def only ever renders a row
 * that has not been migrated yet, and re-labelling it would restate the new
 * vocabulary on the one surface still describing pre-rename state. It also keeps
 * `WIDGET_LABELS` purely additive, and that catalogue reaches the database as a
 * widget row's `name` when Player AI creates one.
 *
 * Retired by PB-191 Task 33 in a LATER release, triggered by Task 4's own
 * assertion — zero rows holding the old literal — holding in every environment.
 * `availableForNewInstances: false` on its dashboard contract is what keeps this
 * a rendering fallback rather than a second thing a user can add.
 *
 * @deprecated Use {@link lamaCategoryResultsDef}.
 */
export const lamaPillarResultsDef: SqlWidgetDef<
  LamaPillarResultsParams,
  LamaPillarResultsOutput
> = {
  ...lamaCategoryResultsDef,
  kind: "lamaPillarResults",
  label: "Results by Pillar",
  subtitle: "Current, previous, delta, and sparkline per pillar",
};

registerWidget(lamaCategoryResultsDef);
registerWidget(lamaPillarResultsDef);
